function detectUniformType(name, uniforms) {
  let typeName = uniforms[name];
  const isFunc = typeof typeName === "function";
  if (isFunc) {
    const sample = typeName(0);
    typeName = (Array.isArray(sample) ? sample.length : 1) + "f";
  }
  return {
    typeName,
    isFunc,
    isArray: typeName !== "1f",
  };
}

function buildFragmentShader(config) {
  const uniformDecl = Object.keys(config.uniforms)
    .map((uniformName) => {
      const uniformType = detectUniformType(uniformName, config.uniforms).typeName[0];
      return `uniform ${uniformType > 1 ? `vec${uniformType}` : "float"} ${uniformName};`;
    })
    .join("\n");

  let src = `\n${uniformDecl}\n`;
  if (config.textures && config.textures.length) {
    src += `\nuniform sampler2D tex[${config.textures.length}];\n`;
  }
  src += config.mainCode;
  return src;
}

function prepareConfig(mainCode, props) {
  const config = props || {};
  config.size = config.size || [0, 0];
  config.mainCode = mainCode;
  config.uniforms = config.uniforms || {};
  config.uniforms.time = "1f";
  config.uniforms.resolution = "2f";
  config.vertexShader =
    config.vertexShader ||
    "attribute vec2 vert;\nvoid main(void) { gl_Position = vec4(vert, 0.0, 1.0);}";

  if (
    config.mainCode.indexOf("void main()") === -1 &&
    config.mainCode.indexOf("void main(void)") === -1
  ) {
    config.mainCode = `\nvoid main() {\n${config.mainCode}\n}`;
  }

  config.fragmentShader = config.fragmentShader || buildFragmentShader(config);
  if (config.fragmentShader.trim().indexOf("precision") !== 0) {
    config.fragmentShader = `precision ${config.precision || "highp"} float;\n${config.fragmentShader}`;
  }
  return config;
}

function createShader(gl, program, src, shaderType, debug) {
  const id = gl.createShader(shaderType);
  gl.shaderSource(id, src);
  gl.compileShader(id);
  const message = gl.getShaderInfoLog(id);
  if (message) {
    if (debug) {
      // Keep the debug output concise while still giving useful line numbers.
      const numbered = src
        .split("\n")
        .map((line, idx) => `${String(idx + 1).padStart(4, "0")}: ${line}`)
        .join("\n");
      throw new Error(`${message}\n${numbered}`);
    }
    throw new Error(message);
  }
  gl.attachShader(program, id);
}

function svgSupport(url) {
  if (url.indexOf("svg") > -1) {
    if (url.indexOf("xmlns") === -1) {
      url = url.split("<svg ").join('<svg xmlns="http://www.w3.org/2000/svg" ');
    }
    return `data:image/svg+xml;base64,${btoa(url)}`;
  }
  return url;
}

function createTexture(gl, index, image) {
  const texture = gl.createTexture();
  gl.activeTexture(gl.TEXTURE0 + index);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

function handleTextures(gl, program, config) {
  if (!config.textures || !config.textures.length) return;

  gl.uniform1iv(
    gl.getUniformLocation(program, "tex"),
    config.textures.map((_, i) => i)
  );

  config.textures.forEach((source, index) => {
    if (typeof source === "string") {
      const loader = new Image();
      loader.crossOrigin = "anonymous";
      loader.src = svgSupport(source);
      loader.onload = () => createTexture(gl, index, loader);
      return;
    }
    createTexture(gl, index, source);
  });
}

export function createRGBA(mainCode, props) {
  const config = prepareConfig(mainCode, props);
  const canvas = config.target;
  if (!canvas) throw new Error("createRGBA requires a target canvas.");

  const gl = canvas.getContext("webgl", {
    alpha: true,
    antialias: false,
    premultipliedAlpha: true,
  });
  if (!gl) {
    return {
      gl: null,
      destroy: () => {},
    };
  }

  const program = gl.createProgram();
  createShader(gl, program, config.vertexShader, gl.VERTEX_SHADER, config.debug);
  createShader(gl, program, config.fragmentShader, gl.FRAGMENT_SHADER, config.debug);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) || "WebGL program linking failed.");
  }

  gl.useProgram(program);

  const frameCallbacks = config.frame ? [config.frame] : [];
  const uniformSetters = {};

  Object.keys(config.uniforms).forEach((uniformName) => {
    const loc = gl.getUniformLocation(program, uniformName);
    const type = detectUniformType(uniformName, config.uniforms);
    const setter = gl[`uniform${type.typeName}`].bind(gl);
    const assign = type.isArray
      ? (value) => setter(loc, ...value)
      : (value) => setter(loc, value);

    uniformSetters[uniformName] = assign;

    if (!type.isFunc) return;

    let previousValue = config.uniforms[uniformName](0);
    assign(previousValue);
    frameCallbacks.push((timeMs) => {
      const nextValue = config.uniforms[uniformName](previousValue, timeMs);
      previousValue = nextValue;
      assign(nextValue);
    });
  });

  const triangle = new Float32Array([-1, 3, -1, -1, 3, -1]);
  gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
  gl.bufferData(gl.ARRAY_BUFFER, triangle, gl.STATIC_DRAW);
  const vert = gl.getAttribLocation(program, "vert");
  gl.vertexAttribPointer(vert, 2, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(vert);

  handleTextures(gl, program, config);

  const handleSize = () => {
    const width = canvas.clientWidth | 0;
    const height = canvas.clientHeight | 0;
    if (config.size[0] === width && config.size[1] === height) return;
    config.size = [width, height];
    canvas.width = width;
    canvas.height = height;
    gl.viewport(0, 0, width, height);
    if (uniformSetters.resolution) {
      uniformSetters.resolution(config.size);
    }
  };

  let rafId = null;
  let destroyed = false;

  const draw = (timeMs) => {
    if (destroyed) return;
    handleSize();
    if (uniformSetters.time) {
      uniformSetters.time(timeMs / 1000);
    }
    frameCallbacks.forEach((cb) => cb(timeMs));
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (config.loop !== false) {
      rafId = requestAnimationFrame(draw);
    }
  };

  if (config.loop !== false) {
    rafId = requestAnimationFrame(draw);
  } else {
    draw(0);
  }

  return {
    gl,
    setUniform: (name, value) => {
      const setter = uniformSetters[name];
      if (!setter) return;
      setter(value);
    },
    destroy: () => {
      destroyed = true;
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    },
  };
}
